import { mfaMethodLabels, type MfaMethod } from "@purlink/shared/auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { enAU } from "date-fns/locale";
import {
  ChevronDown,
  KeyRound,
  Laptop,
  LockKeyhole,
  LogOut,
  Mail,
  RefreshCw,
  Send,
  Smartphone,
  Tablet,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { useState } from "react";
import { AccountNav } from "~/components/account/account-nav";
import { usePasswordActions } from "~/components/account/password-actions";
import {
  BackupCodesPanel,
  EmailSetup,
  MethodChoice,
  TotpSetup,
} from "~/components/account/mfa-setup";
import { PasswordInput } from "~/components/auth/auth-layout";
import { PageHeader } from "~/components/shell/app-shell";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { ConfirmDialog } from "~/components/ui/confirm-dialog";
import { SimpleDialog } from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/feedback";
import { Field } from "~/components/ui/field";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { toast } from "~/components/ui/toaster";
import { api, ApiError, unwrap } from "~/lib/api";
import { errorMessage, fieldErrorsOf, meQuery, useCurrentUser } from "~/lib/auth";

export const Route = createFileRoute("/_app/account/security")({
  component: SecurityPage,
});

const sessionsQuery = {
  queryKey: ["me", "sessions"] as const,
  queryFn: () => unwrap(api.api.me.sessions.$get()),
};

function SecurityPage() {
  return (
    <>
      <PageHeader title="Account" description="Password, two-step sign-in and signed-in devices." />
      <AccountNav />
      <div className="flex max-w-3xl flex-col gap-6">
        <PasswordCard />
        <TwoStepCard />
        <SessionsCard />
        <DangerZone />
      </div>
    </>
  );
}

function PasswordCard() {
  const { user } = useCurrentUser();
  const { changePassword, sendResetLink, dialogs } = usePasswordActions(user.email);
  return (
    <Card>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-md bg-primary-soft text-primary-soft-fg">
            <LockKeyhole className="size-5" aria-hidden />
          </span>
          <div>
            <h3 className="text-h3 text-fg">Password</h3>
            <p className="text-small text-fg-muted">
              Changing it signs you out of every other device.
            </p>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="sm" className="self-start sm:self-auto">
              Manage password <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuItem onSelect={changePassword}>
              <KeyRound /> Change password…
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={sendResetLink}>
              <Send /> Send password reset link…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {dialogs}
    </Card>
  );
}

/** Irreversible actions, collapsed on every visit so they're hard to hit by accident. */
function DangerZone() {
  const { user } = useCurrentUser();
  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="danger" className="glass border-error/30">
        <AccordionTrigger>
          <span className="flex items-center gap-3">
            <span className="grid size-8 place-items-center rounded-md bg-error-soft text-error">
              <TriangleAlert className="size-4" aria-hidden />
            </span>
            <span className="flex flex-col">
              <span className="text-h3 text-error">Danger zone</span>
              <span className="text-small text-fg-muted">Delete your account</span>
            </span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <DeleteAccount isSuperAdmin={user.role === "super_admin"} />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function TwoStepCard() {
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();
  const current = user.mfaMethod as MfaMethod;
  const [switching, setSwitching] = useState(false);
  const [target, setTarget] = useState<MfaMethod | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [regenerateOpen, setRegenerateOpen] = useState(false);

  const closeSwitch = () => {
    setSwitching(false);
    setTarget(null);
    setCodes(null);
  };

  const Icon = current === "email" ? Mail : Smartphone;

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Two-step sign-in</CardTitle>
          <CardDescription>Required for every Purlink account.</CardDescription>
        </div>
        <Badge tone="success">On</Badge>
      </CardHeader>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-md bg-primary-soft text-primary-soft-fg">
            <Icon className="size-5" aria-hidden />
          </span>
          <div>
            <p className="text-body font-medium text-fg">{mfaMethodLabels[current]}</p>
            <p className="text-small text-fg-muted">
              {current === "email" ? `Codes are sent to ${user.email}` : "Codes come from your app"}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setSwitching(true)}>
            <RefreshCw /> Change method
          </Button>
          <Button variant="secondary" onClick={() => setRegenerateOpen(true)}>
            <KeyRound /> New backup codes
          </Button>
        </div>
      </div>

      <Sheet open={switching} onOpenChange={(open) => (open ? setSwitching(true) : closeSwitch())}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{codes ? "Save your backup codes" : "Change two-step method"}</SheetTitle>
            <SheetDescription>
              {codes
                ? "Your new method is on. Your backup codes are below."
                : "You'll need to confirm the new method works before it replaces the current one."}
            </SheetDescription>
          </SheetHeader>
          {codes ? (
            <BackupCodesPanel codes={codes} onDone={closeSwitch} doneLabel="Done" />
          ) : target === null ? (
            <MethodChoice value={null} current={current} onChange={(method) => setTarget(method)} />
          ) : target === "totp" ? (
            <TotpSetup
              onComplete={async (backupCodes) => {
                setCodes(backupCodes);
                await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
              }}
            />
          ) : (
            <EmailSetup
              email={user.email}
              onComplete={async (backupCodes) => {
                setCodes(backupCodes);
                await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
              }}
            />
          )}
        </SheetContent>
      </Sheet>

      <RegenerateBackupCodes open={regenerateOpen} onOpenChange={setRegenerateOpen} />
    </Card>
  );
}

function RegenerateBackupCodes({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string>();
  const [codes, setCodes] = useState<string[] | null>(null);

  const regenerate = useMutation({
    mutationFn: () => unwrap(api.api.me.mfa["backup-codes"].$post({ json: { password } })),
    onSuccess: (result) => setCodes(result.backupCodes),
    onError: (err) => setError(fieldErrorsOf(err).password ?? errorMessage(err)),
  });

  const close = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      setPassword("");
      setError(undefined);
      setCodes(null);
    }
  };

  return (
    <SimpleDialog
      open={open}
      onOpenChange={close}
      title={codes ? "Your new backup codes" : "Generate new backup codes"}
      description={codes ? undefined : "Your old backup codes will stop working."}
    >
      {codes ? (
        <BackupCodesPanel codes={codes} onDone={() => close(false)} doneLabel="Done" />
      ) : (
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            setError(undefined);
            if (!password) setError("Enter your password.");
            else regenerate.mutate();
          }}
        >
          <Field label="Your password" error={error}>
            <PasswordInput
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>
          <Button type="submit" loading={regenerate.isPending}>
            Generate codes
          </Button>
        </form>
      )}
    </SimpleDialog>
  );
}

const deviceIcons = { desktop: Laptop, mobile: Smartphone, tablet: Tablet } as const;

function SessionsCard() {
  const queryClient = useQueryClient();
  const sessions = useQuery(sessionsQuery);

  const revoke = useMutation({
    mutationFn: (id: string) => unwrap(api.api.me.sessions[":id"].$delete({ param: { id } })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionsQuery.queryKey });
      toast.success("Device signed out.");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Signed-in devices</CardTitle>
          <CardDescription>Sign out of any device you don't recognise.</CardDescription>
        </div>
      </CardHeader>
      {sessions.isPending ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : sessions.isError ? (
        <p className="text-body text-error">Couldn't load your devices.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {sessions.data.sessions.map((session) => {
            const DeviceIcon = deviceIcons[session.deviceType];
            return (
              <li key={session.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                <DeviceIcon className="size-5 shrink-0 text-fg-muted" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-body font-medium text-fg">
                    {session.browser} on {session.os}
                    {session.current && (
                      <Badge tone="primary" className="ml-2 align-middle">
                        This device
                      </Badge>
                    )}
                  </p>
                  <p className="truncate text-small text-fg-muted">
                    Active{" "}
                    {formatDistanceToNow(new Date(session.lastActiveAt), {
                      addSuffix: true,
                      locale: enAU,
                    })}
                    {session.ipAddress && ` · ${session.ipAddress}`}
                  </p>
                </div>
                {!session.current && (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={revoke.isPending && revoke.variables === session.id}
                    onClick={() => revoke.mutate(session.id)}
                  >
                    <LogOut /> Sign out
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function DeleteAccount({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: () => unwrap(api.api.me.delete.$post({ json: { password } })),
    onSuccess: () => window.location.assign("/login?reason=deleted"),
    onError: (err) =>
      setError(
        err instanceof ApiError && err.body?.code === "LAST_SUPER_ADMIN"
          ? err.body.message
          : errorMessage(err),
      ),
  });

  return (
    <div className="flex flex-col gap-4 border-t border-error/20 pt-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-small text-fg-secondary">
        Permanently remove your account. Notes and emails you sent stay on leads under your name.
        {isSuperAdmin && " The last remaining Super Admin can't be deleted."}
      </p>
      <Button
        variant="destructive"
        size="sm"
        className="shrink-0 self-start sm:self-auto"
        onClick={() => setOpen(true)}
      >
        <Trash2 /> Delete my account
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setPassword("");
            setError(null);
          }
        }}
        title="Delete your account?"
        description="This can't be undone. You'll be signed out immediately."
        confirmLabel="Delete account"
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          setError(null);
          if (!password) {
            setError("Enter your password to confirm.");
            return;
          }
          remove.mutate();
        }}
      >
        <Field label="Your password" error={error ?? undefined}>
          <PasswordInput
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
      </ConfirmDialog>
    </div>
  );
}
